import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card, useToast } from '@kss/ui';
import { ArrowRight, RefreshCw } from 'lucide-react';
import { CrudList } from '../shared/CrudPage';
import { invoiceService, paymentService, enumLabel } from '@kss/api';
import type { Invoice, Payment, PaymentCreate, PaymentFilter } from '@kss/api';
import { NameCell } from '../../hooks/useNameMaps';

/**
 * Live `Payment` row uses `mode` (int enum) and `referenceNumber`
 * (verified 2026-05-05). The legacy `method` / `reference` keys
 * never resolved on real rows — both columns rendered '—'. This
 * file now reads from the canonical names with defensive fallbacks
 * to the legacy aliases for any pre-migration cached data.
 *
 * SSO Issue #10a (2026-07-13) — surfaces a "Pending Receivables"
 * panel above the standard Payments grid so cashiers can jump
 * straight from an unpaid invoice into a pre-filled Payment form.
 * Rows are open invoices (`balanceAmount > 0` and `status != 4`
 * Cancelled — matches the AR aging report's convention).
 */
export default function AllPayments() {
  return (
    <div className="space-y-4">
      <PendingReceivables />
      <CrudList<Payment, PaymentCreate, PaymentFilter>
        title="All payments" newLabel="New payment" service={paymentService}
        idKey="paymentId" labelKey="paymentNumber" basePath="/panel/payments"
        // Newest payment on top — paymentNumber embeds a date prefix that
        // sorts wrong chronologically ascending. Ref: ORDER_WORKFLOW_AUDIT #6.
        initialSortStatus={{ columnAccessor: 'paymentDate', direction: 'desc' }}
        columns={[
          { accessor: 'paymentNumber', title: 'Number', sortable: true, filterable: true, width: 140, render: (r) => r.paymentNumber || '—' },
          { accessor: 'paymentDate', title: 'Date', sortable: true, render: (r) => r.paymentDate ? new Date(r.paymentDate).toLocaleDateString() : '—' },
          {
            accessor: 'mode',
            title: 'Method',
            sortable: true,
            filterable: true,
            render: (r) => {
              // PaymentMode enum: 1=Cash, 2=Card, 3=UPI, 4=NetBanking, 5=Cheque, 6=Wallet, 7=Other
              const mode = r.mode ?? r.method;
              if (mode == null || mode === '') return '—';
              const n = Number(mode);
              return Number.isFinite(n) ? enumLabel('PaymentMode', n) : String(mode);
            },
          },
          {
            accessor: 'referenceNumber',
            title: 'Reference',
            sortable: true,
            filterable: true,
            render: (r) => r.referenceNumber || r.reference || '—',
          },
          { accessor: 'amount', title: 'Amount (₹)', sortable: true, textAlign: 'right', render: (r) => Number(r.amount ?? 0).toLocaleString('en-IN') },
          { accessor: 'invoiceId', title: 'Invoice', render: (r) => <NameCell kind="invoice" id={r.invoiceId} /> },
        ]}
      />
    </div>
  );
}

const PENDING_LIMIT = 20;

function PendingReceivables() {
  const navigate = useNavigate();
  const toast = useToast();

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const rows = await invoiceService.all();
      setInvoices(Array.isArray(rows) ? rows : []);
      setLoaded(true);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load invoices';
      setErr(msg);
      toast.error('Load failed', msg);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // Load once on mount; a Refresh button re-fetches on demand.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { pending, total } = useMemo(() => {
    /* Open receivables — same filter as the AR aging report:
       skip Cancelled (status == 4) and skip rows that don't
       carry a positive outstanding balance. Newest invoice
       first so the top of the panel is what the cashier just
       booked. */
    const openRows = invoices.filter((inv) => {
      // 5 = Cancelled (was 4, which is Overdue — see enums.ts InvoiceStatusLabels).
      if (Number(inv.status ?? 0) === 5) return false;
      return Number(inv.balanceAmount ?? 0) > 0;
    });
    openRows.sort((a, b) => {
      const da = String(a.invoiceDate ?? '');
      const db = String(b.invoiceDate ?? '');
      // Descending — DESC by ISO date compares as string just fine.
      if (da < db) return 1;
      if (da > db) return -1;
      return 0;
    });
    return { pending: openRows.slice(0, PENDING_LIMIT), total: openRows.length };
  }, [invoices]);

  const totalOutstanding = useMemo(
    () => pending.reduce((s, r) => s + Number(r.balanceAmount ?? 0), 0),
    [pending],
  );

  function recordPayment(inv: Invoice) {
    /* Deep-link into the payment form with the invoice + customer
       + outstanding balance pre-populated. The form reads these
       via useSearchParams on mount. */
    const params = new URLSearchParams();
    if (inv.invoiceId) params.set('invoiceId', inv.invoiceId);
    if (inv.customerId) params.set('customerId', inv.customerId);
    const bal = Number(inv.balanceAmount ?? 0);
    if (Number.isFinite(bal) && bal > 0) params.set('amount', String(bal));
    navigate(`/panel/payments/new?${params.toString()}`);
  }

  return (
    <Card
      title="Pending Receivables"
      subtitle={
        loaded
          ? `${total} open invoice${total === 1 ? '' : 's'} · ₹${totalOutstanding.toLocaleString('en-IN')} outstanding`
          : 'Loading open invoices…'
      }
      action={
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      }
      padded={false}
    >
      {err ? (
        <div className="px-4 py-3 text-sm text-danger">{err}</div>
      ) : loading && pending.length === 0 ? (
        <div className="px-4 py-6 text-center text-sm text-content-muted">Loading…</div>
      ) : pending.length === 0 ? (
        <div className="px-4 py-6 text-center text-sm text-content-muted">
          {loaded ? 'No open invoices — every customer is up to date.' : ' '}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-border bg-surface-muted/40 text-left text-xs font-semibold uppercase tracking-wide text-content-secondary">
                  <th className="px-3 py-2">Invoice #</th>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Customer</th>
                  <th className="px-3 py-2 text-right">Total (₹)</th>
                  <th className="px-3 py-2 text-right">Paid (₹)</th>
                  <th className="px-3 py-2 text-right">Outstanding (₹)</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {pending.map((inv) => {
                  const bal = Number(inv.balanceAmount ?? 0);
                  const tot = Number(inv.totalAmount ?? 0);
                  const paid = Number(inv.paidAmount ?? Math.max(0, tot - bal));
                  return (
                    <tr key={inv.invoiceId ?? inv.invoiceNumber} className="border-b border-border/60 last:border-b-0 hover:bg-surface-card-hover">
                      <td className="px-3 py-2 font-mono text-xs">{inv.invoiceNumber ?? '—'}</td>
                      <td className="px-3 py-2">{inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString() : '—'}</td>
                      {/* SSO ISSUES 2 & 3 — storefront invoices stamp
                          `customerId = user.id` and never create a
                          Customer row, so the map lookup misses.
                          NameCell now falls back to "Guest customer"
                          instead of rendering blank. */}
                      <td className="px-3 py-2"><NameCell kind="customer" id={inv.customerId} fallback="Guest customer" /></td>
                      <td className="px-3 py-2 text-right">{tot.toLocaleString('en-IN')}</td>
                      <td className="px-3 py-2 text-right">{paid.toLocaleString('en-IN')}</td>
                      <td className="px-3 py-2 text-right font-semibold">{bal.toLocaleString('en-IN')}</td>
                      <td className="px-3 py-2 text-right">
                        <Button size="sm" onClick={() => recordPayment(inv)}>
                          Record Payment
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {total > PENDING_LIMIT && (
            <div className="flex items-center justify-end gap-1 border-t border-border px-3 py-2 text-xs">
              <span className="text-content-muted">
                Showing top {PENDING_LIMIT} of {total}.
              </span>
              <Link
                to="/panel/invoices"
                className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
              >
                See all invoices <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
